import mixpanel from 'mixpanel-browser';
import { FC, useEffect, useState } from 'react';
import { useFormContext } from '../../../../../../contexts/FormContext';
import {
  calculateFleschReadingEase,
  calculateReadingTime,
} from '../../../../../../lib/readability/TextStats';
import { BasicButton } from '../../../../../shared/buttons/BasicButton';

import styles from './ReadabilityScore.module.css';

type Readability = {
  fleschReadingEase: number;
  readingTime: number;
};

const readabilityScores: string[] = [
  '90 - 100',
  '80 - 89',
  '70 - 79',
  '60 - 69',
  '50 - 59',
  '30 - 49',
  '0 - 30',
];

const readabilityDescriptions: string[] = [
  'Very easy to read',
  'Easy to read',
  'Fairly easy to read',
  'Basic understanding',
  'Fairly difficult to read',
  'Difficult to read',
  'Very difficult to read',
];

const readabilityLevels: string[] = [
  '5th grade',
  '6th grade',
  '7th grade',
  '8th or 9th grade',
  '10th or 12th grade',
  'College',
  'College graduate',
];

const ReadabilityTable = () => {
  useEffect(() => {
    mixpanel.track('View readability table');
  }, []);

  return (
    <div id="readability-table" className={styles['table-container']}>
      <table>
        <thead>
          <tr>
            <th>
              <p>Score</p>
            </th>
            <th>
              <p>Readability</p>
            </th>
            <th>
              <p>Reading Level</p>
            </th>
          </tr>
        </thead>
        <tbody>
          {readabilityScores.map((score, index) => {
            const readabilityDescription = readabilityDescriptions[index];
            const readabilityLevel = readabilityLevels[index];
            return (
              <tr key={index}>
                <td>
                  <p>{score}</p>
                </td>
                <td>
                  <p>{readabilityDescription}</p>
                </td>
                <td>
                  <p>{readabilityLevel}</p>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export const ReadabilityScore: FC = () => {
  const { editedOutput } = useFormContext();

  const [showReadabilityTable, setShowReadabilityTable] =
    useState<boolean>(false);
  const [readability, setReadability] = useState<Readability | undefined>();
  const [loading, setLoading] = useState<boolean>(false);

  const monitorClick = (e: MouseEvent) => {
    const readabilityButton = document.getElementById('readability-button');
    const readabilityTable = document.getElementById('readability-table');
    if (!readabilityButton || !readabilityButton) return;
    if (
      !readabilityButton.contains(e.target as Node) &&
      !readabilityTable?.contains(e.target as Node)
    ) {
      setShowReadabilityTable(false);
    }
  };

  useEffect(() => {
    if (showReadabilityTable) {
      window.addEventListener('click', monitorClick);
      return () => window.removeEventListener('click', monitorClick);
    }
  }, [showReadabilityTable]);

  useEffect(() => {
    if (editedOutput.length > 0) {
      setLoading(true);
      const calculateReadabilityTimeout = setTimeout(() => {
        const fleschReadingEase = calculateFleschReadingEase(
          editedOutput[0].text,
          0
        );
        const readingTime = calculateReadingTime(editedOutput[0].text, 0);
        setReadability({
          fleschReadingEase,
          readingTime,
        });
        setLoading(false);
      }, 1500);
      return () => clearTimeout(calculateReadabilityTimeout);
    }
  }, [editedOutput]);

  if (editedOutput.length === 0) return null;

  return (
    <div className={styles['container']}>
      <div className={styles['readability-button']}>
        <BasicButton
          id="readability-button"
          type="button"
          loading={loading}
          disabled={loading}
          text={
            loading
              ? ''
              : `${!readability ? '' : readability.fleschReadingEase}`
          }
          // text="Very difficult to read"
          image={{
            src: '/icons/BookBlack.svg',
            height: 16,
            width: 16,
          }}
          onClick={() => setShowReadabilityTable(!showReadabilityTable)}
          extraStyles={{
            height: '30px',
            backgroundColor: 'inherit',
          }}
        />{' '}
      </div>
      {showReadabilityTable && <ReadabilityTable />}
    </div>
  );
};
